import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/shared/ui';

import { authApi } from '../api/authApi';
import type { StepUpAction } from '../api/types';
import { Alert } from '@/shared/ui';
import { TotpCodeForm } from '../components/TotpCodeForm';
import { errorCodeOf, useApiErrorMessage } from '@/shared/lib/useApiErrorMessage';

import {
  StepUpContext,
  StepUpCancelledError,
  STEP_UP_REUSE_SKEW_MS,
  type StepUpContextValue
} from './stepUpContext';

interface PendingRequest {
  /** Identity of this prompt, used to remount the code field between requests. */
  id: number;
  action: StepUpAction;
  resolve: (token: string) => void;
  reject: (reason: unknown) => void;
}

let requestCounter = 0;

interface CachedToken {
  token: string;
  /** Epoch ms. */
  expiresAt: number;
}

/**
 * Hosts the step-up dialog and hands out `requestStepUp`.
 *
 * The shape is "imperative promise wrapping a declarative dialog": a caller in
 * the middle of a mutation says `const token = await requestStepUp('money')`
 * and either gets a token or a `StepUpCancelledError`. It does not have to own
 * dialog state, wire an `onConfirm` callback, or thread a token back up through
 * three components — which is what makes this reusable from *any* mutation
 * without each one growing its own copy of the flow.
 *
 * Design decisions worth defending:
 *
 *  - **Tokens are cached per action class, never globally.** The backend binds a
 *    step-up to one action, so a token minted for `card-sensitive` must not be
 *    offered to a `money` call — it would fail server-side anyway, but caching it
 *    globally would mean prompting the user and then failing, which is worse than
 *    just prompting.
 *  - **Cache hits are checked against the clock with a 30s skew**, so a token
 *    that will expire mid-flight is treated as absent.
 *  - **One request at a time.** A second `requestStepUp` while the dialog is
 *    open queues behind the first rather than racing it; TOTP codes are
 *    single-use, so two dialogs would guarantee one of them collects
 *    `2fa_code_already_used`.
 *  - The dialog is `closeOnBackdrop={false}`: this interrupts a money operation,
 *    and dismissing it by a stray click on the overlay is not an intention.
 */
export function StepUpProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const messageOf = useApiErrorMessage();

  const [pending, setPending] = useState<PendingRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorNonce, setErrorNonce] = useState(0);

  // Refs, not state: the cache and the queue must never trigger a render, and
  // reading them must never see a stale closure.
  const cacheRef = useRef(new Map<StepUpAction, CachedToken>());
  const queueRef = useRef<PendingRequest[]>([]);

  const openNext = useCallback(() => {
    const next = queueRef.current.shift() ?? null;
    setPending(next);
    setError(null);
    setSubmitting(false);
  }, []);

  const requestStepUp = useCallback((action: StepUpAction): Promise<string> => {
    const cached = cacheRef.current.get(action);
    if (cached && cached.expiresAt - STEP_UP_REUSE_SKEW_MS > Date.now()) {
      return Promise.resolve(cached.token);
    }
    cacheRef.current.delete(action);

    return new Promise<string>((resolve, reject) => {
      requestCounter += 1;
      const request: PendingRequest = { id: requestCounter, action, resolve, reject };
      // `setPending` via the updater form so a burst of synchronous calls
      // cannot both observe `null` and both open a dialog.
      setPending((current) => {
        if (current !== null) {
          queueRef.current.push(request);
          return current;
        }
        setError(null);
        setSubmitting(false);
        return request;
      });
    });
  }, []);

  const reset = useCallback(() => {
    cacheRef.current.clear();
    for (const queued of queueRef.current) queued.reject(new StepUpCancelledError());
    queueRef.current = [];
    setPending((current) => {
      current?.reject(new StepUpCancelledError());
      return null;
    });
  }, []);

  const cancel = useCallback(() => {
    pending?.reject(new StepUpCancelledError());
    openNext();
  }, [pending, openNext]);

  const submit = useCallback(
    (code: string) => {
      if (pending === null) return;
      const request = pending;

      setSubmitting(true);
      setError(null);

      authApi
        .stepUp({ code, action: request.action })
        .then((result) => {
          cacheRef.current.set(request.action, {
            token: result.stepUpToken,
            expiresAt: Date.parse(result.expiresAt)
          });
          request.resolve(result.stepUpToken);
          openNext();
        })
        .catch((cause: unknown) => {
          // Same three-way distinction as the sign-in challenge: a spent code is
          // not a wrong code, and telling the user otherwise sends them to debug
          // an authenticator that is working perfectly.
          const code_ = errorCodeOf(cause);
          setError(
            code_ === '2fa_code_already_used'
              ? t('auth.twoFactor.errorReplayed')
              : code_ === 'invalid_2fa_code'
                ? t('auth.twoFactor.errorInvalid')
                : code_ === '2fa_not_configured'
                  ? t('auth.twoFactor.errorNotConfigured')
                  : messageOf(cause)
          );
          setErrorNonce((n) => n + 1);
          setSubmitting(false);
        });
    },
    [pending, openNext, t, messageOf]
  );

  const value = useMemo<StepUpContextValue>(
    () => ({ requestStepUp, reset }),
    [requestStepUp, reset]
  );

  return (
    <StepUpContext.Provider value={value}>
      {children}

      {/*
        Mounted only while a request is pending. A permanently-mounted <dialog>
        keeps its title and description in the document even when closed, which
        leaves "Confirm this operation" sitting in the accessibility tree of
        every screen in the app.
      */}
      {pending !== null && (
        <Modal
          open
          onClose={cancel}
          title={t('stepUp.title')}
          description={t('stepUp.description')}
          closeOnBackdrop={false}
        >
          <div className="flex flex-col gap-4">
            <Alert tone="info">{t(`stepUp.reason.${pending.action}`)}</Alert>

            {/*
              Keyed by request id: when a queued request opens after the first
              one resolves, the field must come up EMPTY. Reusing the instance
              would leave the previous — now spent — code sitting in the boxes.
            */}
            <TotpCodeForm
              key={pending.id}
              onSubmit={submit}
              submitting={submitting}
              error={error ?? undefined}
              errorNonce={errorNonce}
              submitLabel={t('stepUp.submit')}
            />
          </div>
        </Modal>
      )}
    </StepUpContext.Provider>
  );
}
